import { useEffect, useRef, useState } from "react";
import {
  FileText,
  Siren,
  Microscope,
  Gauge,
  FlaskConical,
  ShieldCheck,
  GitCompare,
  Radar,
  Wrench,
} from "lucide-react";

export const SECTION_META = {
  overview: { label: "Overview", icon: FileText },
  priority: { label: "Patch priority", icon: Siren },
  rootcause: { label: "Root cause", icon: Microscope },
  exploitability: { label: "Exploitability", icon: Gauge },
  pseudo: { label: "Pseudo-exploit", icon: FlaskConical },
  assessment: { label: "Assessment kit", icon: ShieldCheck },
  diff: { label: "Patch diff", icon: GitCompare },
  detection: { label: "Detection rules", icon: Radar },
  mitigations: { label: "Mitigations", icon: Wrench },
};

const OFFSET = 128;

function useScrollSpy(ids) {
  const [active, setActive] = useState(ids[0]);
  useEffect(() => {
    const onScroll = () => {
      let current = ids[0];
      for (const id of ids) {
        const el = document.getElementById(`sec-${id}`);
        if (el && el.getBoundingClientRect().top - OFFSET <= 8) current = id;
      }
      const atBottom = window.innerHeight + window.scrollY >= document.body.scrollHeight - 4;
      setActive(atBottom ? ids[ids.length - 1] : current);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [ids]);
  return [active, setActive];
}

function jumpTo(id) {
  const el = document.getElementById(`sec-${id}`);
  if (!el) return;
  const top = el.getBoundingClientRect().top + window.scrollY - OFFSET + 16;
  window.scrollTo({ top, behavior: "smooth" });
}

export function AnalysisNav({ cveId, ids, footer }) {
  const [active, setActive] = useScrollSpy(ids);
  const chipsRef = useRef(null);

  useEffect(() => {
    const chip = chipsRef.current?.querySelector(`[data-id="${active}"]`);
    chip?.scrollIntoView({ block: "nearest", inline: "center", behavior: "smooth" });
  }, [active]);

  const go = (id) => {
    setActive(id);
    jumpTo(id);
  };

  return (
    <>
      <aside data-testid="analysis-sidebar" className="hidden lg:block">
        <div className="sticky top-20">
          <div className="px-3 mb-3">
            <div className="text-xs text-slate-500">Analysis</div>
            <div className="font-mono text-sm font-semibold text-slate-900 truncate">{cveId}</div>
          </div>
          <nav className="flex flex-col gap-0.5">
            {ids.map((id) => {
              const { label, icon: Icon } = SECTION_META[id];
              const on = active === id;
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => go(id)}
                  data-testid={`nav-section-${id}`}
                  aria-current={on ? "true" : undefined}
                  className={`relative flex items-center gap-2.5 px-3 py-2 rounded-md text-sm text-left transition-colors ${
                    on
                      ? "bg-violet-50 text-violet-800 font-semibold"
                      : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"
                  }`}
                >
                  {on && <span className="absolute left-0 top-1.5 bottom-1.5 w-0.5 rounded-full bg-violet-700" />}
                  <Icon className={`h-4 w-4 ${on ? "text-violet-700" : "text-slate-400"}`} />
                  {label}
                </button>
              );
            })}
          </nav>
          {footer && <div className="mt-5 pt-4 border-t border-slate-200 px-1">{footer}</div>}
        </div>
      </aside>

      <div
        data-testid="analysis-mobile-nav"
        className="lg:hidden sticky top-14 z-20 -mx-4 md:-mx-8 px-4 md:px-8 bg-slate-50/95 backdrop-blur border-b border-slate-200"
      >
        <div ref={chipsRef} className="flex gap-1.5 overflow-x-auto py-2.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {ids.map((id) => {
            const { label } = SECTION_META[id];
            const on = active === id;
            return (
              <button
                key={id}
                data-id={id}
                type="button"
                onClick={() => go(id)}
                data-testid={`mobile-nav-section-${id}`}
                className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                  on
                    ? "bg-violet-700 border-violet-700 text-white"
                    : "bg-white border-slate-200 text-slate-600 hover:text-slate-900"
                }`}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>
    </>
  );
}
